import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import queryString from 'query-string'

import { fetchVoiceAgents } from '@/reducers/agentProvisioning/agentProvisioning.actions'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'

// Resolves the provisioning identity of the agent open on the detail page:
// its slug, Cartesia agent id (the "Cartesia agent ID" column on the list
// page), and organization name. The provisioning record is matched by the
// by-user route's userId — a voice_agents row is per ai-agent user, so this
// is the only unambiguous key (one Studio agent can fan out to several
// Cartesia agents). On the legacy /:agentId route everything resolves to
// null and callers hide what's missing.
export const useAgentIdentifiers = () => {
  const dispatch = useDispatch()
  const { userId } = useParams()
  const location = useLocation()
  const agents = useSelector((state) => state.agentProvisioning.agents)
  const agentsLoading = useSelector((state) => Boolean(state.agentProvisioning.loading.agents))
  const detailLoading = useSelector((state) => Boolean(state.voiceAgents.loading?.agentDetail))
  const organizations = useSelector((state) => state.organizations)
  const ownOrgId = useSelector((state) => state.currentUser.organizationid)

  // Admin org scope threaded from the list (`?org=`); non-admins carry no
  // param and the BE scopes to their own org.
  const { org } = queryString.parse(location.search)

  const record = userId ? agents.items.find((agent) => agent.user_id === Number(userId)) : undefined

  // The agent's own config, loaded by the route itself. It carries the owning
  // `voiceAgentId` and `organizationId` (AVR-463), resolved server-side from
  // the agent rather than from the caller — so these are right even for a Balto
  // admin who arrived with no `?org=` scope and whose roster is therefore their
  // own org's. Without this fallback the provisioning record is the only source,
  // and everything keyed on it — the rename control included — silently
  // disappears on a deep link (AVR-827).
  const detail = useSelector((state) => getSelectedAgent(state.voiceAgents))
  const detailOrgId = detail?.organizationId ?? null

  // The roster is still worth fetching: it is where the agent's owner name
  // comes from. Scope it to the agent's OWN org, not the caller's, or an admin
  // viewing a customer's agent gets a roster this agent is not in. The org is
  // only known once the config lands, so the attempt is keyed by scope rather
  // than by a single "tried once" flag — the first pass may legitimately have
  // nothing better than the caller's own org to go on.
  const attemptedScopesRef = useRef(new Set())
  useEffect(() => {
    if (!userId || record || agentsLoading) {
      return
    }
    const scope = org ? Number(org) : detailOrgId
    // Wait for the config rather than firing an unscoped request we would only
    // have to repeat — unless it has already resolved without an org (a 404,
    // i.e. a retired or unseeded agent), in which case the caller's own org is
    // the best available scope.
    if (!scope && detailLoading) {
      return
    }
    const key = String(scope ?? 'self')
    if (attemptedScopesRef.current.has(key)) {
      return
    }
    attemptedScopesRef.current.add(key)
    dispatch(fetchVoiceAgents(scope || undefined))
  }, [dispatch, userId, record, agentsLoading, org, detailOrgId, detailLoading])

  const orgId = record?.organization_id ?? detailOrgId ?? (org ? Number(org) : ownOrgId)
  const organizationName =
    organizations.find((organization) => organization.id === orgId)?.name ?? null

  return {
    organizationName,
    organizationId: orgId ?? null,
    // The voice_agents master-row id — the key the call-data-deletion routes
    // take, and a different id space from `cartesiaAgentId` below.
    voiceAgentId: record?.id ?? detail?.voiceAgentId ?? null,
    cartesiaAgentId: record?.configs?.[0]?.agent_id ?? null,
    slug: record?.slug ?? null,
    // The agent's display name, and the two columns a rename writes (AVR-827).
    // It lives on the agent's own ai-agent user because a voice_agent is
    // one-to-one with that user — the same reason the agents list labels its
    // rows with it.
    ownerFirstName: record?.owner_first_name ?? null,
    ownerLastName: record?.owner_last_name ?? null,
    // The ai-agent user this voice agent runs as. `call_ingestion_worker`
    // attributes every processed call to `voice_agent.user.id`, so this is the
    // id Call Explorer's `agents` filter matches on — which is what makes a
    // "show me this agent's calls" link possible at all.
    agentUserId: userId ? Number(userId) : null,
  }
}
